import { animated, useSpring } from 'react-spring';
import useMeasure from 'react-use-measure';
import { ResizeObserver } from '@juggle/resize-observer';
import { ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';
import { CgSpinner } from '@react-icons/all-files/cg/CgSpinner';

interface ExpandProps {
  children: ReactNode;
  isOpen: boolean;
  disableSlideContent?: boolean;
  expandDown?: boolean;
  className?: string;
  startingHeight?: number;
  isChildLoading?: boolean;
  loadingClassname?: string;
  duration?: number;
}

export function SpinningLoader(props: { loadingClassname?: string }) {
  return (
    <div
      className={classNames(
        'h-full flex justify-center items-center',
        props?.loadingClassname,
      )}
    >
      <div className="w-full flex justify-center items-center">
        <CgSpinner className="animate-spin text-4xl" />
      </div>
    </div>
  );
}

export function Expand({
  children,
  isOpen,
  disableSlideContent,
  expandDown = true,
  className,
  startingHeight,
  isChildLoading,
  loadingClassname,
  duration,
}: ExpandProps) {
  const [measureRef, { height }] = useMeasure({
    polyfill: ResizeObserver,
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const animatedHeight = useSpring({
    to: {
      height: isOpen ? height : 0,
    },
    from: {
      height: startingHeight || 0,
    },
    config: duration ? { duration } : undefined,
  });

  const animatedTranslation = useSpring({
    to: {
      transform: !disableSlideContent
        ? isOpen
          ? 'translateY(0px)'
          : `translateY(${-100}px)`
        : 'none',
      opacity: isOpen ? 1 : 0,
    },
    onRest: () => setIsAnimating(false),
  });

  useEffect(() => {
    setIsAnimating(true);
  }, [isOpen]);

  return (
    <animated.div
      style={isChildLoading && loadingClassname ? {} : animatedHeight}
      className={classNames(isAnimating && 'overflow-hidden', 'h-full')}
    >
      <animated.div style={animatedTranslation} ref={measureRef as any}>
        <div className={className}>{(isOpen || isAnimating) && children}</div>
      </animated.div>
      {isChildLoading && <SpinningLoader loadingClassname={loadingClassname} />}
    </animated.div>
  );
}
